import {Component, Input, ViewChild, Inject} from '@angular/core';
import {DomSanitizer, SafeHtml, SafeUrl, TransferState, makeStateKey} from '@angular/platform-browser';
import {HttpClient} from '@angular/common/http';

import {OpenAPIService} from '../services/openapi';
import {UtilsService} from '../services/utils';
import {SearchService} from '../services/search';
import {RoutesService} from '../services/routes';
import {MenuService} from '../services/menu';
import {PlatformService} from '../services/platform.service';

declare let window:any;

const LOCATIONS = ['body', 'formData', 'query', 'path', 'header'];
const SORT_LOCATIONS = (l1, l2) => {
  return LOCATIONS.indexOf(l1) - LOCATIONS.indexOf(l2);
}

@Component({
    selector: 'documentation',
    templateUrl: '../views/documentation/main.pug',
    styles: [`
      .discussion-frame {
        border: 1px solid #333;
        width: 100%;
        height: 400px;
      }
      .docs-contents, search-results {
        max-width: 800px;
      }
    `],
})
export class DocumentationComponent {
  title:string = 'Documentation';
  activeItem:any;
  activeHTML:string;
  showResults:boolean;
  discussionURL:SafeUrl;

  @ViewChild('htmlContents') htmlContents;
  subscriptions:any[] = [];

  error:string;

  constructor(private sanitizer:DomSanitizer,
              public platformService: PlatformService,
              public search:SearchService,
              private menu:MenuService,
              private routes:RoutesService,
              private state:TransferState,
              private http:HttpClient,
              public openapi:OpenAPIService,
              public utils:UtilsService,
              @Inject('ORIGIN_URL') private originUrl:string) {}

  ngAfterViewInit() {
    this.subscriptions.push(this.search.results.subscribe(res => {
      this.showResults = !!this.search.query;
      if (!this.showResults && this.activeItem) this.setActiveItem(this.activeItem);
    }))
    this.subscriptions.push(this.menu.choose.subscribe(item => {
      if (!item && this.menu.items.length) {
        this.menu.setActiveItem(this.menu.items[0]);
      } else if (item) {
        this.setActiveItem(item);
      }
    }));
    this.subscriptions.push(this.routes.currentRoute.subscribe(route => {
      if (route && route.title) this.title = route.title;
    }));
    if (this.menu.currentChoice) {
      this.setActiveItem(this.menu.currentChoice);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  maybeResizeIframe() {
    let frame = window.jQuery('iframe.discussion-frame');
    frame.css('height', frame[0].contentWindow.document.body.scrollHeight);
  }

  setActiveItem(item) {
    this.error = null;
    this.showResults = false;
    this.activeItem = item;
    const stateKey = makeStateKey(item.markdownURL || '');
    if (item.contents) {
      this.setActiveItemHTML(item.contents, !item.isHTML);
    } else if (item.markdownURL) {
      item.isHTML = !!item.markdownURL.match(/\.html$/);
      item.contents = this.state.get(stateKey, null as any);
      if (!item.contents) {
        this.http.get(this.originUrl + item.markdownURL, {responseType: 'text'})
          .toPromise()
          .then((md: any) => {
            item.contents = md;
            this.setActiveItemHTML(item.contents, !item.isHTML);
          })
          .catch(e => {
            this.error = (e.error && e.error.message) || "Unknown error.";
          })
      } else {
        this.setActiveItemHTML(item.contents, !item.isHTML);
      }
    }
    if (this.platformService.isServer) {
      this.state.set(stateKey, item.contents as any);
    }

    if (this.platformService.isBrowser) {
      setTimeout(() => {
        // delay discussionURL for a sec to help page loading.
        let discussionURL = this.activeItem.discussionURL || window.config.discussionURL;
        if (discussionURL && this.activeItem.discussionURL !== null) {
          this.discussionURL = this.getDiscussionURL(discussionURL, item.title);
        } else {
          this.discussionURL = null;
        }
      }, 1000);
    }
  }

  setActiveItemHTML(html, renderMarkdown=true) {
    if (renderMarkdown) {
      let title = this.activeItem.title.replace(/\W/g, '.');
      let titleRegex = new RegExp('^#+\\s+' + title + '\s*([\n\r]|$)');
      html = html.replace(titleRegex, '');
      html = window.marked(html);
    } else {
      html = html.replace(/(<script[^>]*>)/g, '\n\n\$1').replace(/(<\/script>)/g, '\$1\n\n');
    }
    let newElem = window.document.createElement('div');
    newElem.innerHTML = html;
    if (newElem.innerHTML !== this.activeHTML) {
      this.htmlContents.nativeElement.innerHTML = '';
      this.activeHTML = newElem.innerHTML;
      if (this.platformService.isServer) {
        this.htmlContents.nativeElement.innerHTML = newElem.innerHTML;
      } else {
        window.jQuery(this.htmlContents.nativeElement).html(newElem.innerHTML);
      }
    }
  }

  getDiscussionURL(url, title):SafeUrl {
    url = url.replace(/{{\s*title\s*}}/g, title);
    let slug = title.replace(/\W+/g, '_');
    url = url.replace(/{{\s*slug\s*}}/g, slug);
    url = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    return url;
  }
}

@Component({
  selector: 'operation-documentation',
  templateUrl: '../views/documentation/operation.pug',
  styles: [`
    .try-it-button {
      margin-bottom: 30px;
    }
    .nav-tabs {
      margin-bottom: 15px;
    }
    .status-code-tabs {
      font-weight: bold;
    }
    .parameter {
      margin-bottom: 20px;
    }
    .parameter-details {
      padding-left: 15px;
    }
    .input-output-box {
      border: 1px solid #333;
      border-radius: 4px;
      padding: 15px;
      margin-top: 15px;
    }
    .input-output-box h4 {
      margin-bottom: 20px;
    }
  `],
})
export class OperationDocumentationComponent {
  @Input() apiCall;
  @ViewChild('apiCallComponent') apiCallComponent;
  locations:string[];
  activeLocation:string;
  responses:any[];
  activeResponse:any;
  tryIt:boolean;
  marked:Function = window.marked;

  constructor(public utils:UtilsService,
              public openapi:OpenAPIService,
              private routes:RoutesService) {}

  ngOnChanges() {
    this.locations = this.apiCall.operation.parameters.map(p => p.in);
    this.locations = this.locations.filter((loc, idx) => {
      return idx === this.locations.lastIndexOf(loc);
    }).sort(SORT_LOCATIONS);
    this.activeLocation = this.locations[0];

    this.responses = Object.keys(this.apiCall.operation.responses).map(code => {
      return {
        code: code,
        details: this.apiCall.operation.responses[code],
      }
    }).sort((r1, r2) => {
      if (r1.code > r2.code) return 1;
      if (r1.code < r2.code) return -1;
      return 0;
    })
    this.activeResponse = this.responses[0];
  }
}



// WEBPACK FOOTER //
// ./src/app/components/documentation.ts